import baseApi from "utils/axios-instance";

// Types (matching backend models)
export interface KnownQuestion {
  id: string;
  question_text: string;
  category: string;
  expected_response_type: string;
  results_field_path: string | null;
  procedure_code: string | null;
  is_custom: boolean;
}

export interface CallScriptResponse {
  call_script_id: string | null;
  name: string;
  ordered_question_ids: string[];
  questions: KnownQuestion[];
  additional_info?: string | null;
  creation_time_ts?: string | null;  // ISO format datetime string
  last_updated_ts?: string | null;  // ISO format datetime string
}

export interface CallScriptListResponse {
  call_scripts: CallScriptResponse[];
}

export interface SaveCallScriptRequest {
  team_id: string;
  call_script_id?: string | null;
  name: string;
  ordered_question_ids: string[];
  additional_info?: string | null;
  questions: Array<{
    id?: string;
    question_text: string;
    category: string;
    expected_response_type: string;
    results_field_path?: string | null;
    procedure_code?: string | null;
    is_custom: boolean;
  }>;
}

export interface SaveCallScriptResponse {
  is_successful: boolean;
  call_script_id: string;
  created_questions: string[];
  updated_questions: string[];
}

/**
 * List all call scripts for a team
 */
export const getCallScripts = async (teamId: string): Promise<CallScriptResponse[]> => {
  const response = await baseApi.get<CallScriptListResponse>(
    `/dashboard/call_scripts?team_id=${teamId}`,
    { withCredentials: true }
  );
  return response.data.call_scripts;
};

/**
 * Get a specific call script by ID
 */
export const getCallScript = async (callScriptId: string): Promise<CallScriptResponse> => {
  const response = await baseApi.get<CallScriptResponse>(
    `/dashboard/call_scripts/${callScriptId}`,
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Save a call script (create or update)
 */
export const saveCallScript = async (request: SaveCallScriptRequest): Promise<SaveCallScriptResponse> => {
  const response = await baseApi.post<SaveCallScriptResponse>(
    "/dashboard/call_scripts",
    request,
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Create a call script from the default template with specified procedure codes
 */
export interface CreateFromTemplateRequest {
  team_id: string;
  name: string;
  procedure_codes: string[];
}

export const createCallScriptFromTemplate = async (
  request: CreateFromTemplateRequest
): Promise<SaveCallScriptResponse> => {
  const response = await baseApi.post<SaveCallScriptResponse>(
    "/dashboard/call_scripts/create_from_template",
    request,
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Delete a call script
 */
export const deleteCallScript = async (callScriptId: string): Promise<void> => {
  await baseApi.delete(`/dashboard/call_scripts/${callScriptId}`, {
    withCredentials: true,
  });
};
